import { useMemo } from 'react';
import { type FilterOptions, type FilterSummary } from '../types/filters';
import { useProductGridCommon, useProductDataProcessing } from './shared/useProductGridCommon';
import { addFilterParametersToRequest } from '../utils/collectionProductApiHelpers';
import type { ProductSearchRequest } from '@skin-seoul/service/src/infrastructure/product/utils/types';

interface UseSearchProductGridDataProps {
  searchQuery: string;
  filters?: FilterOptions;
  sortBy?: string;
  currentPage?: number;
  productsPerPage: number;
  onProductCountChange?: (count: number) => void;
  onFilterSummaryChange?: (summary: FilterSummary | undefined) => void;
}

/**
 * Custom hook to manage search product grid data fetching and processing
 * Similar to useProductGridData but uses the search API endpoint
 */
export const useSearchProductGridData = ({
  searchQuery,
  filters = {},
  sortBy,
  currentPage = 1,
  productsPerPage,
  onProductCountChange,
  onFilterSummaryChange,
}: UseSearchProductGridDataProps) => {
  const { currency, productService, pagination } = useProductGridCommon({
    currentPage,
    productsPerPage,
  });

  const searchParams = useMemo((): ProductSearchRequest => {
    const params: ProductSearchRequest = {
      q: searchQuery,

      page: pagination.page,
      limit: pagination.limit,

      publicationStatus: 'publish' as const,
      stockStatus: 'instock' as const,
      currency,

      sortBy: sortBy || 'BestSeller',
    };

    addFilterParametersToRequest(params, filters);

    return params;
  }, [searchQuery, filters, currency, pagination, sortBy]);

  const { data } = productService.useGetProductSearch(searchParams);

  // Use shared data processing and side effects
  const gridData = useProductDataProcessing({
    data,
    currentPage,
    onProductCountChange,
    onFilterSummaryChange,
  });

  return {
    ...gridData,
    searchQuery: data?.searchQuery || searchQuery,
  };
};
